import ProgressCard from 'components/card/ProgressCard'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ContributorAction from 'referencial/components/contributor/actions/ContributorAction'
import SupportAction from 'referencial/components/support/actions/SupportAction'
import TaxonAction from 'referencial/components/taxon/actions/TaxonAction'
import i18n from 'simple-react-i18n'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import useTitle from 'utils/customHook/useTitle'
import HydrobioMonitoringFilter from './HydrobioMonitoringFilter'
import HydrobioMonitoringTable from './HydrobioMonitoringTable'
import { getStationTitleWithTownName } from 'utils/StationUtils'
import OperationAction from 'quality/components/operation/actions/OperationAction'
import HydrobioMonitoringGraph from './HydrobioMonitoringGraph'
import { hasValue } from 'utils/NumberUtil'
import queryString from 'query-string'
import IndiceMonitoringTable from './IndiceMonitoringTable'
import QualityAction from 'quality/actions/QualityAction'
import { useLocation, useParams } from 'react-router'
import { calculateTaxonThresholdsResult, calculateThresholdsResult } from 'utils/QualityThresholdUtils'
import { Tab, Tabs } from '@mui/material'
import { THRESHOLD } from 'quality/constants/QualityConstants'

const TAB_RESULT = 'result'
const TAB_GRAPH = 'graph'
const TAB_INDICE = 'indice'

const calculateTotal = (list, phases) => {
    const { enumerationA = 0, enumerationB = 0, enumerationC = 0, enumerationC2 = 0 } = list
    if (phases.length === 0) return enumerationA + enumerationB + enumerationC + enumerationC2
    const eA = phases.includes(1) ? enumerationA : 0
    const eB = phases.includes(2) ? enumerationB : 0
    const eC = phases.includes(3) ? enumerationC : 0
    const eC2 = phases.includes(4) ? enumerationC2 : 0
    return eA + eB + eC + eC2
}

const HydrobioMonitoringApp = () => {
    const dispatch = useDispatch()
    const { id: idStation } = useParams()
    const location = useLocation()
    const query = queryString.parse(location.search)

    const {
        hydrobioOperations,
        hydrobioLists,
        qualitometer,
        searchIndices,
        qualityThresholds,
        citiesIndex,
    } = useSelector(store => ({
        hydrobioOperations: store.OperationReducer.hydrobioOperations,
        hydrobioLists: store.OperationReducer.hydrobioLists,
        qualitometer: store.QualityReducer.qualitometer,
        searchIndices: store.QualityReducer.searchIndices,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const [filter, setFilter] = useState({
        startDate: parseInt(query.startDate) || undefined,
        endDate: parseInt(query.endDate) || undefined,
        producers: [],
        samplers: [],
        determiners: [],
        phases: [],
    })
    const [activeTab, setActiveTab] = useState(TAB_RESULT)
    /*
    filter = {
        startDate, // number
        endDate, // number
        taxons, // array[string]
        indices, // array[String]

        taxonSelection, // number
        taxonSelectionResults, // array of string
        taxonThreshold, // number
        indiceSelection, // number
        indiceSelectionResults, // array of string
        indiceThreshold, // number

        producers, // array[number]
        samplers, // array[number]
        determiners, // array[number]
        point,
        support, // string
        phases, // array[number]
    }
    */

    const townName = useMemo(() => citiesIndex[qualitometer.townCode]?.name, [citiesIndex, qualitometer])

    useTitle(() => [{
        title: i18n.quality,
        href: 'quality',
    }, {
        title: getStationTitleWithTownName(qualitometer, townName),
        href: `station/quality/${idStation}`,
    }, {
        title: i18n.hydrobioMonitoring,
        href: `station/quality/${idStation}/hydrobioMonitoring`,
    }], [qualitometer, townName])

    const { progress, isLoaded } = useProgressDispatch(() => {
        return [
            dispatch(ContributorAction.fetchContributors()),
            dispatch(TaxonAction.fetchTaxons()),
            dispatch(SupportAction.fetchSupports()),
            dispatch(OperationAction.fetchRemarks()),
            dispatch(OperationAction.fetchQualitometerHydrobioOperations(idStation)),
            dispatch(OperationAction.fetchAllHydrobioList(idStation)),
        ]
    })

    useEffect(() => {
        if (qualitometer?.code) {
            dispatch(QualityAction.fetchSearchIndices({ stations: [qualitometer.code] }))
        }
    }, [dispatch, qualitometer.code])

    const hydrobioListsFormatted = useMemo(() => {
        const threshold = hasValue(filter.taxonThreshold) ? qualityThresholds.find(t => t.thresholdCode === `${filter.taxonThreshold}` && t.thresholdType === THRESHOLD.TAXON) : undefined
        return hydrobioLists.map(list => {
            const operation = hydrobioOperations.find(o => o.id === list.operation)
            return {
                ...list,
                date: operation?.date,
                support: hasValue(operation?.support) ? `${operation?.support}` : undefined,
                total: calculateTotal(list, filter.phases),
                ...calculateTaxonThresholdsResult(list, threshold?.thresholds),
                // startDate: operation?.startDate,
                // endDate: operation?.endDate,
            }
        })
    }, [filter.phases, filter.taxonThreshold, hydrobioLists, hydrobioOperations, qualityThresholds])

    const indicesFormatted = useMemo(() => {
        const threshold = hasValue(filter.indiceThreshold) ? qualityThresholds.find(t => t.thresholdCode === `${filter.indiceThreshold}` && t.thresholdType === THRESHOLD.INDICE) : undefined
        return searchIndices.map(i => ({
            ...i,
            ...calculateThresholdsResult(i, threshold?.thresholds),
        }))
    }, [filter.indiceThreshold, qualityThresholds, searchIndices])

    const hydrobioOperationsFiltered = useMemo(() => {
        const startDateFilter = filter.startDate ? hydrobioOperations.filter(o => o.date >= filter.startDate) : hydrobioOperations
        const endDateFilter = filter.endDate ? startDateFilter.filter(o => o.date <= filter.endDate) : startDateFilter
        const supportFilter = filter.support ? endDateFilter.filter(o => `${o.support}` === filter.support) : endDateFilter

        const producerFilter = filter.producers.length !== 0 ? supportFilter.filter(o => filter.producers.includes(o.producer)) : supportFilter
        const samplerFilter = filter.samplers.length !== 0 ? producerFilter.filter(o => filter.samplers.includes(o.sampler)) : producerFilter
        return filter.determiners.length !== 0 ? samplerFilter.filter(o => filter.determiners.includes(o.determiner)) : samplerFilter
    }, [filter.determiners, filter.endDate, filter.producers, filter.samplers, filter.startDate, filter.support, hydrobioOperations])

    const hydrobioListsFiltered = useMemo(() => {
        const operationFilter = hydrobioListsFormatted.filter(l => hydrobioOperationsFiltered.some(o => o.id === l.operation))

        const selectionFilter = filter.taxonSelection !== '-1' && filter.taxonSelectionResults ? operationFilter.filter(a => filter.taxonSelectionResults.includes(a.taxon)) : operationFilter
        return filter.taxons?.length ? selectionFilter.filter(l => filter.taxons.includes(l.taxon)) : selectionFilter
    }, [filter.taxonSelection, filter.taxonSelectionResults, filter.taxons, hydrobioListsFormatted, hydrobioOperationsFiltered])

    const filteredIndices = useMemo(() => {
        const operationFilter = indicesFormatted.filter(i => hydrobioOperationsFiltered.some(o => o.id === i.idOperation && o.qualitometer === i.stationId))

        const selectionFilter = filter.indiceSelection !== '-1' && filter.indiceSelectionResults ? operationFilter.filter(a => filter.indiceSelectionResults.includes(a.parameter)) : operationFilter
        return filter.indices?.length ? selectionFilter.filter(i => filter.indices.includes(i.parameter)) : selectionFilter
    }, [filter.indiceSelection, filter.indiceSelectionResults, filter.indices, hydrobioOperationsFiltered, indicesFormatted])

    return (
        <div style={{ padding: '0 10 0 20' }}>
            {
                !isLoaded && <ProgressCard progress={progress} />
            }
            {
                isLoaded && (
                    <div className='row no-margin'>
                        <HydrobioMonitoringFilter
                            onValidate={setFilter}
                            defaultFilter={filter}
                        />
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => setActiveTab(newValue)}
                            variant='fullWidth'
                        >
                            <Tab value={TAB_RESULT} label={i18n.result} />
                            <Tab value={TAB_GRAPH} label={i18n.graph} />
                            <Tab value={TAB_INDICE} label={i18n.indice} />
                        </Tabs>

                        <>
                            {activeTab === TAB_RESULT && (
                                <HydrobioMonitoringTable
                                    hydrobioLists={hydrobioListsFiltered}
                                    hydrobioOperations={hydrobioOperationsFiltered}
                                />
                            )}

                            {activeTab === TAB_GRAPH && (
                                <HydrobioMonitoringGraph
                                    hydrobioLists={hydrobioListsFiltered}
                                    hydrobioOperations={hydrobioOperationsFiltered}
                                    indices={filteredIndices}
                                />
                            )}

                            {activeTab === TAB_INDICE && (
                                <IndiceMonitoringTable
                                    indices={filteredIndices}
                                    operations={hydrobioOperationsFiltered}
                                />
                            )}
                        </>
                    </div>
                )
            }
        </div>
    )
}

export default HydrobioMonitoringApp